/**
 * Layout — navigation visibility helpers.
 *
 * Extracted from `Layout.jsx` (no behavior change). Two new helpers
 * for the route-level guard:
 *   • hasWorkspaceAccess(user) — true when the user carries any
 *     non-HR workspace role/permission.
 *   • hasHrPortalAccess(user)  — true when the user carries
 *     `hr.self_service` or any HR portal permission.
 */

// Workspace roles — owners/admins always pass. The rest mirror the
// `roles` arrays sprinkled across `NAV_GROUPS` so this list is the
// single source of truth used by the App-level guard.
export const WORKSPACE_ROLES = new Set([
  "Owner", "Admin", "Sales Manager", "Sales Executive",
  "Operations Manager", "Consultant", "Compliance Reviewer",
  "Document Controller", "Finance Manager", "Finance User",
  "Renewal Manager", "Trainer", "PRO Executive", "Auditor",
  "HR Manager",  // HR Manager *also* has workspace nav access
]);

// Workspace-flavored permission keys — any presence on the user signals
// real operational scope (we keep the list small and explicit).
export const WORKSPACE_PERMS_HINT = [
  "leads.read", "quotations.read", "cases.read",
  "customers.read", "documents.read", "finance.read",
  "reports.read", "settings.read",
];

export function hasWorkspaceAccess(user) {
  if (!user) return false;
  if (user.is_owner) return true;
  const roles = user.roles || [];
  if (roles.some((r) => WORKSPACE_ROLES.has(r))) return true;
  const perms = user.permissions || [];
  return WORKSPACE_PERMS_HINT.some((k) => perms.includes(k));
}

export function hasHrPortalAccess(user) {
  if (!user) return false;
  const perms = user.permissions || [];
  // Any HR permission (self-service, leave, attendance, payslips, etc.)
  // lights up the My HR portal.
  return perms.includes("hr.self_service") || perms.some((p) => p.startsWith("hr."));
}

// ─── Original Layout.jsx helpers (unchanged behavior) ───────────────
export function navAllowedForUser(item, user, canHR) {
  // Phase HR-1A — HR-sensitive items opt out of the generic role
  // matching: they MUST be evaluated through `canHR()` so Admin doesn't
  // auto-pass (payroll/salary data stays out of ordinary Admin scope).
  if (item.hrPerm) {
    return typeof canHR === "function" && canHR(item.hrPerm);
  }
  if (item.hrPermAny) {
    if (typeof canHR !== "function") return false;
    return (item.hrPermAny || []).some((k) => canHR(k));
  }
  if (item.roles === "*") return true;
  if (!user) return false;
  if (user.is_owner) return true;
  const userRoles = user.roles || [];
  return (item.roles || []).some((r) => userRoles.includes(r));
}

export function navEntitled(item, sub) {
  if (!item.feature) return true;
  if (!sub || sub.loading || !sub.entitlements) return true;
  return sub.feature(item.feature);
}

export function visibleItems(items, user, sub, canHR) {
  return items.filter((it) => navAllowedForUser(it, user, canHR) && navEntitled(it, sub));
}

// Phase MODULE-PRICING (2026-02-27) — module gating.
// A group is visible only when:
//   • it has no `module` field (shared groups always show), OR
//   • the subscription snapshot lists that module in enabled_modules
//     (canonical source since 2026-07-06), OR
//   • the user's legacy `enabled_modules` contains that module (kept
//     for back-compat with pre-snapshot tenants).
// Legacy users without `enabled_modules` (pre-backfill) default to
// both modules so nothing is hidden by surprise.
export function navGroupAllowedForModule(group, user, sub) {
  if (!group || !group.module) return true;
  // Canonical source — tenant_subscriptions snapshot via context.
  if (sub && !sub.loading && sub.hasModule) {
    return sub.hasModule(group.module);
  }
  const mods = (user && user.enabled_modules) || ["certification", "hr"];
  return mods.includes(group.module);
}
